import { Control }           from 'react-hook-form';
import { createListCollection } from '@chakra-ui/react';
import { useFieldArray }      from 'react-hook-form';
import ApiCollection          from '@widesk-core/models/ApiCollection';
import LinkTypeModel          from '@/models/services/website/LinkTypeModel';
import React                  from 'react';
import StandPageLinkModel     from '@/models/services/website/StandPageLinkModel';
import StandPageModel         from '@/models/services/website/StandPageModel';
import useFetch               from '@widesk-core/hooks/useFetch';
import { FormValues }         from './standFormTypes';
import type { SocialNetworkRow } from './standFormTypes';

export const useStandPageLinks = (activeStandPage: StandPageModel | null, control: Control<FormValues>) => {

	// ─── Fetches ─────────────────────────────────────────────────────────────────

	const { data: linkTypeModels, isLoading: linkTypesLoading } = useFetch(async () => {
		const collection = await new ApiCollection(LinkTypeModel).list();
		return collection.models;
	}, true);

	const linkTypes = React.useMemo(
		() => linkTypeModels
			? createListCollection({ items: linkTypeModels.map(lt => ({ label: lt.name, value: lt.urn })) })
			: undefined,
		[linkTypeModels],
	);

	// Résout l'urn du type de lien d'un standPageLink depuis la liste déjà chargée (pas de fetch de relation).
	const linkTypeUrnOf = React.useCallback(
		(spl: StandPageLinkModel) => linkTypeModels?.find(lt => lt.id === spl.getId('linkType'))?.urn ?? '',
		[linkTypeModels],
	);

	const { data: standPageLinks, isLoading: standPageLinksLoading } = useFetch(async () => {
		if (!activeStandPage?.id) return [];
		const collection = await new ApiCollection(StandPageLinkModel)
			.setFilter('standPage', [activeStandPage.id])
			.list();
		return collection.models;
	}, true);

	// ─── Field array & synchronisation ───────────────────────────────────────────

	const socialNetworksField = useFieldArray({ control, name: 'socialNetworks' });
	const standPageLinksRef = React.useRef<StandPageLinkModel[]>([]);

	React.useEffect(() => {
		if (!standPageLinks || !linkTypeModels) return;
		standPageLinksRef.current = standPageLinks;
		socialNetworksField.replace(
			standPageLinks.length > 0
				? standPageLinks.map(spl => ({ linkTypeUrn: linkTypeUrnOf(spl), url: spl.url.replace(/^https?:\/\//, '') }))
				: [{ linkTypeUrn: '', url: '' }]
		);
	}, [standPageLinks, linkTypeModels]);

	// ─── Sauvegarde ──────────────────────────────────────────────────────────────

	const saveStandPageLinks = async (socialNetworks: SocialNetworkRow[], currentStandPage: StandPageModel, needsNewStandPage: boolean) => {
		const validRows = socialNetworks.filter(row => row.linkTypeUrn && row.url);
		const currentLinks = standPageLinksRef.current;

		// 1 - Créer/mettre à jour chaque lien valide (en parallèle)
		//     Nouveau standPage → toujours créer de nouveaux liens, ne pas déplacer les anciens (historique)
		const patchPromises = validRows.map(async row => {
			const existing = needsNewStandPage ? undefined : currentLinks.find(spl => linkTypeUrnOf(spl) === row.linkTypeUrn);
			const model = existing ?? new StandPageLinkModel();
			await model.patch({ linkType: row.linkTypeUrn, standPage: currentStandPage.urn, url: `https://${row.url}` });
			return model;
		});

		// 2 - Supprimer les liens retirés (seulement si on reste sur le même standPage)
		const destroyPromises = needsNewStandPage
			? []
			: currentLinks
				.filter(spl => !validRows.some(row => row.linkTypeUrn === linkTypeUrnOf(spl)))
				.map(spl => spl.destroy());

		const [savedModels] = await Promise.all([Promise.all(patchPromises), Promise.all(destroyPromises)]);
		standPageLinksRef.current = savedModels;
	};

	return {
		linkTypes,
		linkTypesLoading,
		standPageLinksLoading,
		socialNetworksField,
		saveStandPageLinks,
	};
};
